/**
 * Signup_Screen.tsx — SK Platform
 *
 * ONE-FLOW SIGNUP. Everything the organisation needs to exist is collected on
 * this single card and sent in one anonymous POST /v1/auth/signup: who the
 * person is, what the business is called, how to reach them, and which region
 * their data lives in. The server creates the account complete and stamps
 * onboarding as done, so there is no "Tell us about you" step afterwards.
 *
 * ── WHAT DID NOT CHANGE: THE VERIFICATION GATE ────────────────────────────
 *
 * The org is still born `inactive` and still only activates when the emailed
 * link is clicked, and this screen still ends on the check-email card. What was
 * removed is the second FORM, not the gate — a complete profile on an
 * unverified account buys no access, because every operational endpoint sits
 * behind requireActiveOrg. Anyone editing this screen should keep that split
 * in mind: it is safe to ask for more up front, it is not safe to skip the
 * email.
 *
 * ── FIELD ORDER IS DELIBERATE ─────────────────────────────────────────────
 *
 * Identity (name) → credentials (email, password) → contact (mobile) → the
 * organisation (region, company). It runs from the facts a person answers
 * without thinking to the one that carries a permanent consequence, so the
 * region panel is read by someone who has already settled in rather than
 * someone still deciding whether to sign up.
 *
 * All API calls go through Auth_Service → Api_Endpoints (no hardcoded URLs).
 */

import React, { useState, useRef, useEffect } from 'react';
import { View, Text, StyleSheet, Platform, Animated, Pressable } from 'react-native';

// ── Components ────────────────────────────────────────────────────────────
import AuthBrandHeader from '../../components/authBrandHeader';
import AuthSplitLayout, { useAuthPanelVisible } from '../../components/authSplitLayout';
import CommonInput from '../../components/commonInput';
import EmailComponent from '../../components/emailInput';
import PasswordInput from '../../components/passwordInput';
import MobileNumberInput, { COUNTRIES, CountryEntry } from '../../components/mobileNumberInput';
import BusinessRegionSelect, {
  BUSINESS_REGIONS,
  DEFAULT_BUSINESS_REGION,
  type BusinessRegion,
} from '../../components/businessRegionSelect';
import PrimaryButton from '../../components/primaryButton';

// ── Navigation ──────────────────────────────────────────────────────────────
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { AuthStackParamList } from '../../navigation/types';
import { CONTENT } from '../../utils/responsive';
import { setCheckEmailDeepLink } from '../../navigation/linking';
import { persistPaidProductHandoff, restoreHandoff } from '../../navigation/productHandoff';

// ── Service layer ─────────────────────────────────────────────────────────
import { fetchPrepaidOrder, signupOrg } from '../../services/auth/auth';
import { clearPrepaidClaim, readPrepaidClaim } from '../../navigation/prepaidOrder';

// ── Handlers ──────────────────────────────────────────────────────────────
import { handleApiError } from '../../utils/errorHandler';
import { showSuccess } from '../../utils/successHandler';
import { useToast } from '../../components/toast';

// ── Schema types ──────────────────────────────────────────────────────────
import { SignupPayload, type PrepaidOrderView } from '../../schema/auth/signup';

// ── Validations ───────────────────────────────────────────────────────────
import * as SignupValidations from '../../validations/signupValidations';

// ── Draft persistence (keeps the form across navigation until signup) ──────
import { getSignupDraft, setSignupDraft, clearSignupDraft } from './signupDraft';

// ── Theme ─────────────────────────────────────────────────────────────────
import { useTheme } from '../../context/themeContext';

/**
 * The phone's country is a SEPARATE fact from the business region — it sets a
 * dial code, nothing else, and it defaults to India (+91) independently of
 * whichever region is selected.
 *
 * These two controls are deliberately NOT linked. Seeding the dial code from
 * the region would mean a user who picks a region AFTER typing nothing sees
 * their phone country silently change under them — and it would overwrite this
 * India default the moment the region was touched. Keeping them independent is
 * also what stops the two being conflated, which is the old bug: a UAE operator
 * who typed a local number used to register as India, because one control was
 * setting both facts.
 */
const DEFAULT_PHONE_COUNTRY: CountryEntry =
  COUNTRIES.find((c) => c.code === 'IN') ?? COUNTRIES[0];

/**
 * The phone the buyer typed on the website, as the signup form holds it: a
 * dial-code entry and the local digits. `+971501234567` → UAE + 501234567; a
 * number with no `+` is local to the market it was bought in.
 */
function phoneForForm(phone: string, market: string): { country: CountryEntry; mobile: string } {
  if (phone.startsWith('+')) {
    const match = [...COUNTRIES]
      .sort((a, b) => b.dialCode.length - a.dialCode.length)
      .find((c) => phone.startsWith(c.dialCode));
    if (match) return { country: match, mobile: phone.slice(match.dialCode.length) };
  }
  return { country: COUNTRIES.find((c) => c.code === market) ?? DEFAULT_PHONE_COUNTRY, mobile: phone.replace(/^\+/, '') };
}

const Signup_Screen: React.FC = () => {
  const { theme } = useTheme();
  const { colors } = theme;
  const toast = useToast();
  const navigation = useNavigation<NativeStackNavigationProp<AuthStackParamList, 'signup'>>();

  // Restore any in-progress draft so data survives navigating away and back.
  const savedDraft = getSignupDraft();

  // On wide screens the black panel carries the brand, so the card must not
  // repeat it. See authSplitLayout.
  const showCardBrand = !useAuthPanelVisible();

  const [form, setForm] = useState(
    savedDraft?.form ?? {
      firstName:       '',
      lastName:        '',
      email:           '',
      password:        '',
      confirmPassword: '',
      mobile:          '',
      // Dubai by default — the primary market.
      region:          DEFAULT_BUSINESS_REGION,
      // India (+91) by default, and unaffected by the region above.
      phoneCountry:    DEFAULT_PHONE_COUNTRY,
      businessName:    '',
    },
  );

  // Paid on the website before signing up (Cashfree's return URL): the order
  // shown above the form, and sent with the signup so the new org starts on it.
  const [prepaid, setPrepaid] = useState<PrepaidOrderView | null>(null);
  useEffect(() => {
    const claim = readPrepaidClaim();
    if (!claim) return;
    let alive = true;
    fetchPrepaidOrder(claim.orderId, claim.token)
      .then((order) => {
        if (!alive) return;
        if (order.status === 'claimed' || order.status === 'expired') {
          clearPrepaidClaim();
          return;
        }
        setPrepaid(order);
        // Prefill only what the person has not typed yet.
        const [first, ...rest] = order.buyer.name.trim().split(/\s+/);
        const phone = phoneForForm(order.buyer.phone, order.market);
        const region = BUSINESS_REGIONS.find((r) => r.country === order.market);
        setForm((f: typeof form) => ({
          ...f,
          firstName: f.firstName || first || '',
          lastName: f.lastName || rest.join(' '),
          email: f.email || order.buyer.email,
          mobile: f.mobile || phone.mobile,
          phoneCountry: f.mobile ? f.phoneCountry : phone.country,
          region: region ?? f.region,
        }));
        setIsEmailValid((v) => v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(order.buyer.email));
      })
      .catch(() => {
        // Unknown order or a wrong secret: a plain signup, with no claim sent.
        if (alive) clearPrepaidClaim();
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [touched, setTouched] = useState({
    firstName:       false,
    lastName:        false,
    email:           false,
    password:        false,
    confirmPassword: false,
    mobile:          false,
    businessName:    false,
  });

  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Policy consent — user must agree before an account can be created.
  const [agreedToPolicies, setAgreedToPolicies] = useState(false);
  const [consentTouched, setConsentTouched] = useState(false);
  const consentError = consentTouched && !agreedToPolicies;

  // Spring the checkmark in/out for a premium, tactile toggle.
  const checkScale = useRef(new Animated.Value(agreedToPolicies ? 1 : 0)).current;
  useEffect(() => {
    Animated.spring(checkScale, {
      toValue: agreedToPolicies ? 1 : 0,
      friction: 6,
      tension: 180,
      useNativeDriver: true,
    }).start();
  }, [agreedToPolicies, checkScale]);

  const toggleConsent = () => {
    setConsentTouched(true);
    setAgreedToPolicies((v) => !v);
  };

  // Persist the draft on every change so it survives navigation; cleared once
  // the account is created (see handleCreateAccount).
  useEffect(() => {
    setSignupDraft({ form, step: 1 });
  }, [form]);

  // ── Entrance animation (fade + gentle rise) ───────────────────────────
  const fade = useRef(new Animated.Value(0)).current;
  const rise = useRef(new Animated.Value(14)).current;
  useEffect(() => {
    Animated.parallel([
      Animated.timing(fade, { toValue: 1, duration: 280, useNativeDriver: true }),
      Animated.spring(rise, { toValue: 0, friction: 8, tension: 64, useNativeDriver: true }),
    ]).start();
  }, [fade, rise]);

  /* ── Inline error resolvers ───────────────────────────────────────── */
  const getFirstNameError    = () => SignupValidations.getFirstNameError(form.firstName, touched.firstName);
  const getLastNameError     = () => SignupValidations.getLastNameError(form.lastName, touched.lastName);
  const getBusinessNameError = () => SignupValidations.getBusinessNameError(form.businessName, touched.businessName);
  const getPasswordError     = () => SignupValidations.getPasswordError(form.password, touched.password);
  const getConfirmPasswordError = () =>
    SignupValidations.getConfirmPasswordError(form.password, form.confirmPassword, touched.confirmPassword);
  const getMobileError       = () => SignupValidations.getMobileError(form.mobile, touched.mobile, form.phoneCountry);

  /**
   * Single source of truth for whether the form may be submitted — the same
   * atomic rules the inline errors use, evaluated as if every field were
   * touched, in the order the fields appear on screen. Returning the FIRST
   * failure means the toast points at the topmost problem, which is where the
   * user's eye goes back to.
   *
   * The region needs no rule: it is a closed two-option control that always
   * holds a valid value, so there is no invalid state to report.
   */
  const formError = (): string | null =>
    SignupValidations.getFirstNameError(form.firstName, true) ||
    SignupValidations.getLastNameError(form.lastName, true) ||
    SignupValidations.getEmailError(form.email, isEmailValid, true) ||
    SignupValidations.getPasswordError(form.password, true) ||
    SignupValidations.getConfirmPasswordError(form.password, form.confirmPassword, true) ||
    SignupValidations.getMobileError(form.mobile, true, form.phoneCountry) ||
    SignupValidations.getBusinessNameError(form.businessName, true) ||
    null;

  // Filter input to an allowed set; toast exactly which character isn't allowed.
  const filterNames = (raw: string) => {
    const bad = raw.match(/[^A-Za-z\s]/);
    if (bad) toast.error(`“${bad[0]}” is not allowed — letters only`);
    return raw.replace(/[^A-Za-z\s]/g, '');
  };
  const filterCompany = (raw: string) => {
    const bad = raw.match(new RegExp(SignupValidations.BUSINESS_NAME_DISALLOWED.source));
    if (bad) toast.error(`“${bad[0]}” is not allowed in business name`);
    return raw.replace(SignupValidations.BUSINESS_NAME_DISALLOWED, '');
  };

  /**
   * Region changes touch the region and nothing else — the phone's dial code is
   * the user's own choice and is never rewritten underneath them.
   */
  const handleRegionChange = (region: BusinessRegion) => {
    setForm((f: typeof form) => ({ ...f, region }));
  };

  /* ── Submit — create the whole account in one call ──────────────────── */
  const handleCreateAccount = async () => {
    if (isLoading) return;

    setTouched({
      firstName: true,
      lastName: true,
      email: true,
      password: true,
      confirmPassword: true,
      mobile: true,
      businessName: true,
    });
    setConsentTouched(true);

    const err = formError();
    if (err) {
      toast.error(err);
      return;
    }

    if (!agreedToPolicies) {
      toast.error('Please accept the Terms of Service and Privacy Policy to continue.');
      return;
    }

    setIsLoading(true);
    try {
      const payload: SignupPayload = {
        email:         form.email,
        password:      form.password,
        first_name:    form.firstName,
        last_name:     form.lastName,
        business_name: form.businessName,
        // The org's data-residency region. The server derives the org's ISO
        // country (and therefore its tax pack + currency) from this, so we
        // deliberately do NOT send a `country` — one fact, one authority.
        region:        form.region.code,
        // The PHONE's dial code, which is a different fact from the region.
        country_code:  form.phoneCountry.dialCode,
        phone_number:  form.mobile,
        // Recorded server-side (timestamp + published policy version). The
        // submit is already gated on this below, so it is always true here —
        // it is sent so the acceptance survives as a record rather than only
        // as a button state.
        terms_accepted: agreedToPolicies,
        ...(prepaid ? (() => {
          const claim = readPrepaidClaim();
          return claim ? { prepaid_order_id: claim.orderId, prepaid_token: claim.token } : {};
        })() : {}),
      };

      // Scope the signup to the product the user came from (centralized auth),
      // if any — drives entitlement + the verification-email landing.
      // A website purchase names its product too, with no handoff cookie.
      const originatingProduct = restoreHandoff()?.product ?? prepaid?.plan.productCode;
      const signupRes = await signupOrg(payload, originatingProduct);
      // Account created — the draft has served its purpose; drop it now so a
      // future visit to signup starts fresh.
      clearSignupDraft();
      // The claim went with the signup; it must not ride along a second time.
      clearPrepaidClaim();
      // After verify + sign-in, open the product that was paid for, not the shell.
      if (prepaid) persistPaidProductHandoff(prepaid.plan.productCode);
      showSuccess(
        signupRes,
        'Verification link sent',
        "We've emailed a verification link to your registered email address — open it to verify and continue.",
      );
      // Pin the URL so a refresh on the check-email screen reopens it instead
      // of falling through to the boot probe (which would bounce to login).
      setCheckEmailDeepLink(form.email);
      navigation.navigate('checkEmail', { email: form.email });
    } catch (error: any) {
      handleApiError(error, 'Signup failed', 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthSplitLayout
      headline="Welcome — let's grow your business together"
      sub="We are happy to have you with us. Your journey to a smarter, busier business starts today."
      points={[
        'An AI receptionist that answers every call and books your customers',
        'Bookings, billing, customers and your team in one simple place',
        'Your data stays in the region you choose',
      ]}
    >
      <View style={[s.card, { backgroundColor: colors.card, borderColor: colors.line }]}>
          {showCardBrand ? <AuthBrandHeader /> : null}

          <Text style={[s.title, { color: colors.ink }]}>Create your account</Text>

          {prepaid ? (
            <View
              accessibilityRole="summary"
              style={[s.prepaid, { backgroundColor: colors.accentSoft, borderColor: colors.accentLine }]}
            >
              <Text style={[s.prepaidTitle, { color: colors.ink }]}>
                {prepaid.status === 'paid' ? 'Payment received' : 'Confirming your payment…'}
              </Text>
              <Text style={[s.prepaidText, { color: colors.ink }]}>
                {prepaid.plan.name} · {prepaid.interval === 'year' ? 'yearly' : 'monthly'} · {prepaid.amount.display.total} incl. {prepaid.amount.taxLabel}
              </Text>
              <Text style={[s.prepaidText, { color: colors.ink }]}>
                Create your account and your plan starts straight away.
              </Text>
            </View>
          ) : null}

          <Animated.View style={[s.form, { opacity: fade, transform: [{ translateY: rise }] }]}>
            {/* First name + Last name — one row, as they're read together */}
            <View style={s.row}>
              <View style={[s.col, { marginRight: 8 }]}>
                <CommonInput
                  label="First name"
                  required
                  containerStyle={s.field}
                  placeholder="Enter your first name"
                  value={form.firstName}
                  onChangeText={(t: string) => setForm({ ...form, firstName: filterNames(t) })}
                  touched={touched.firstName}
                  error={getFirstNameError()}
                  inputProps={{ onBlur: () => setTouched((t) => ({ ...t, firstName: true })) }}
                />
              </View>
              <View style={[s.col, { marginLeft: 8 }]}>
                <CommonInput
                  label="Last name"
                  required
                  containerStyle={s.field}
                  placeholder="Enter your last name"
                  value={form.lastName}
                  onChangeText={(t: string) => setForm({ ...form, lastName: filterNames(t) })}
                  touched={touched.lastName}
                  error={getLastNameError()}
                  inputProps={{ onBlur: () => setTouched((t) => ({ ...t, lastName: true })) }}
                />
              </View>
            </View>

            {/* Email */}
            <EmailComponent
              required
              containerStyle={s.field}
              value={form.email}
              onValueChange={(val, valid) => {
                setForm({ ...form, email: val });
                setIsEmailValid(valid);
              }}
              touched={touched.email}
              onBlur={() => setTouched((t) => ({ ...t, email: true }))}
            />

            {/* Password */}
            <PasswordInput
              label="Password"
              required
              containerStyle={s.field}
              placeholder="Enter your password"
              value={form.password}
              onChangeText={(t) => setForm({ ...form, password: t })}
              touched={touched.password}
              error={getPasswordError()}
              showRules={true}
              mode="signup"
              onBlur={() => setTouched((t) => ({ ...t, password: true }))}
            />

            {/* Confirm password — validates live as the user types */}
            <PasswordInput
              label="Confirm Password"
              required
              containerStyle={s.field}
              placeholder="Enter your password again"
              value={form.confirmPassword}
              onChangeText={(t) => {
                setForm({ ...form, confirmPassword: t });
                if (!touched.confirmPassword) setTouched((prev) => ({ ...prev, confirmPassword: true }));
              }}
              touched={touched.confirmPassword}
              error={getConfirmPasswordError()}
              showRules={false}
              mode="login"
              onBlur={() => setTouched((t) => ({ ...t, confirmPassword: true }))}
            />

            {/* Mobile number — its country picker sets the DIAL CODE only.
                Wrapped for the trailing gap: MobileNumberInput is the one field
                that does not own a bottom margin (its helper row "India · 10
                digits" would otherwise sit flush against the next label), and
                it is used on two other screens whose tighter spacing depends
                on that, so the gap is added here rather than in the component. */}
            <View style={s.mobileWrap}>
              <MobileNumberInput
                required
                value={form.mobile}
                selectedCountry={form.phoneCountry}
                onCountryChange={(c: CountryEntry) => setForm({ ...form, phoneCountry: c, mobile: '' })}
                onChangeText={(t: string) => setForm({ ...form, mobile: t })}
                error={getMobileError()}
                onBlur={() => setTouched((t) => ({ ...t, mobile: true }))}
              />
            </View>

            {/* Business region — where the org's data will live. Immutable
                after signup; each option in the dropdown names its own
                consequence (city · privacy law · currency) so the choice is
                informed at the moment it is made. */}
            <BusinessRegionSelect
              required
              containerStyle={s.field}
              value={form.region}
              onChange={handleRegionChange}
            />

            {/* Business name — the org's registered name. Labelled to match the
                validation copy ("Business name is required") and the API field
                (`business_name`); it read "Organization" while every message
                about it said business name. */}
            <CommonInput
              label="Business name"
              required
              containerStyle={s.field}
              placeholder="Enter your business name"
              value={form.businessName}
              onChangeText={(t: string) => setForm({ ...form, businessName: filterCompany(t) })}
              touched={touched.businessName}
              error={getBusinessNameError()}
              inputProps={{ onBlur: () => setTouched((t) => ({ ...t, businessName: true })) }}
            />

            {/* Policy consent — required before creating an account */}
            <Pressable
              onPress={toggleConsent}
              accessibilityRole="checkbox"
              accessibilityState={{ checked: agreedToPolicies }}
              style={({ hovered }: any) => [
                s.consentCard,
                {
                  borderColor: consentError
                    ? colors.dangerLine
                    : agreedToPolicies
                    ? colors.accentLine
                    : colors.line,
                  backgroundColor: consentError
                    ? colors.dangerSoft
                    : agreedToPolicies
                    ? colors.accentSoft
                    : colors.paper,
                },
                Platform.OS === 'web' && hovered ? s.consentCardHover : null,
              ]}
            >
              {/* Custom premium checkbox */}
              <View
                style={[
                  s.checkBox,
                  {
                    borderColor: agreedToPolicies
                      ? colors.accent
                      : consentError
                      ? colors.danger
                      : colors.lineStrong,
                    backgroundColor: agreedToPolicies ? colors.accent : colors.card,
                  },
                  Platform.OS === 'web' && agreedToPolicies
                    ? ({
                        backgroundImage: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentInk} 100%)`,
                        boxShadow: `0 4px 12px ${colors.accent}55`,
                      } as any)
                    : {},
                ]}
              >
                <Animated.View
                  style={[
                    s.checkMark,
                    { borderColor: colors.white, opacity: checkScale, transform: [{ rotate: '45deg' }, { scale: checkScale }] },
                  ]}
                />
              </View>

              {/* Copy */}
              <View style={s.consentTextWrap}>
                <Text style={[s.consentText, { color: colors.ink2 }]}>
                  I agree to Softknack's{' '}
                  <Text style={[s.consentLink, { color: colors.accent }]}>Terms of Service</Text>
                  {' '}and{' '}
                  <Text style={[s.consentLink, { color: colors.accent }]}>Privacy Policy</Text>.
                </Text>
                {consentError ? (
                  <Text style={[s.consentErrorText, { color: colors.danger }]}>
                    Please accept the terms to continue.
                  </Text>
                ) : (
                  <Text style={[s.consentHint, { color: colors.ink4 }]}>
                    Required to create your account.
                  </Text>
                )}
              </View>
            </Pressable>

            {/* Create Account */}
            <View style={s.actionWrap}>
              <PrimaryButton
                title="Create Account"
                onPress={handleCreateAccount}
                loading={isLoading}
                hoverLift
                style={s.createBtn}
                textStyle={s.createBtnText}
              />
            </View>

            {/* Footer — premium divider + sign-in text link */}
            <View style={s.footerDivider}>
              <View style={[s.footerRule, { backgroundColor: colors.line }]} />
              <Text style={[s.footerDividerText, { color: colors.ink4 }]}>ALREADY WITH US?</Text>
              <View style={[s.footerRule, { backgroundColor: colors.line }]} />
            </View>
            <View style={s.footer}>
              <Text style={[s.footerText, { color: colors.ink3 }]}>Have an account? </Text>
              <PrimaryButton
                title="Sign in  →"
                onPress={() => navigation.navigate('login')}
                variant="ghost"
                size="small"
                style={s.footerLinkBtn}
                textStyle={[s.footerLinkText, { color: colors.accent }]}
              />
            </View>
          </Animated.View>
      </View>
    </AuthSplitLayout>
  );
};

/* ── Styles ────────────────────────────────────────────────────────────── */
const s = StyleSheet.create({
  prepaid: {
    borderWidth: 1,
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 14,
    marginBottom: 16,
  },
  prepaidTitle: { fontSize: 15, fontWeight: '600', marginBottom: 4 },
  prepaidText: { fontSize: 14, lineHeight: 20 },
  card: {
    width: '100%',
    maxWidth: CONTENT.form,
    borderRadius: 22,
    borderWidth: 1,
    paddingHorizontal: 26,
    paddingTop: 20,
    paddingBottom: 20,
    overflow: 'hidden',
    ...(Platform.OS === 'web'
      ? ({
          boxShadow:
            '0 24px 60px -18px rgba(15,23,42,0.22), 0 6px 18px -8px rgba(15,23,42,0.10)',
        } as any)
      : { shadowColor: '#0F172A', shadowOffset: { width: 0, height: 14 }, shadowOpacity: 0.14, shadowRadius: 32, elevation: 10 }),
  },
  title: {
    fontSize: 24,
    fontWeight: '900',
    textAlign: 'center',
    letterSpacing: -0.6,
    // Carries the gap the removed subtitle used to provide, so the first field
    // does not sit against the heading.
    marginBottom: 20,
  },
  form: { width: '100%' },
  /**
   * The card's field rhythm. This form carries eight fields plus a consent
   * block, so it is denser than the shared 18 the inputs default to — every
   * field is handed this via `containerStyle` rather than the components being
   * changed, because login / invite-accept / personal-info depend on the
   * roomier default.
   */
  field: {
    marginBottom: 14,
    // Explicit zero because the email field sets `marginVertical`, so without
    // this it alone would keep a 12px TOP gap the other fields don't have and
    // the rhythm would break at exactly one row.
    marginTop: 0,
  },
  /**
   * The mobile field gets MORE than the shared 8, on purpose.
   *
   * It is the only field that ends in a helper line ("India · 10 digits" plus
   * the digit pips) rather than in its input box. Measured from that line, 8px
   * reads as almost nothing and the helper text collides with the BUSINESS
   * REGION label below it — so this field needs a larger number to produce the
   * same apparent gap as its neighbours.
   */
  mobileWrap: { marginBottom: 24 },
  row:  { flexDirection: 'row', width: '100%' },
  col:  { flex: 1 },
  consentCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginTop: 6,
    gap: 9,
    borderWidth: 1,
    borderRadius: 11,
    paddingVertical: 9,
    paddingHorizontal: 10,
    ...(Platform.OS === 'web'
      ? ({ cursor: 'pointer', transition: 'border-color 160ms ease, background-color 160ms ease, box-shadow 160ms ease' } as any)
      : {}),
  },
  consentCardHover: {
    ...(Platform.OS === 'web'
      ? ({ boxShadow: '0 8px 22px -12px rgba(15,23,42,0.18)' } as any)
      : {}),
  },
  checkBox: {
    width: 18,
    height: 18,
    borderRadius: 6,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 1,
    ...(Platform.OS === 'web' ? ({ transition: 'all 160ms ease' } as any) : {}),
  },
  checkMark: {
    width: 5,
    height: 9,
    borderRightWidth: 2,
    borderBottomWidth: 2,
    marginTop: -1.5,
  },
  consentTextWrap: { flex: 1 },
  consentText: {
    fontSize: 12.5,
    fontWeight: '600',
    lineHeight: 18,
  },
  consentLink: {
    fontWeight: '800',
    ...(Platform.OS === 'web' ? ({ textDecorationLine: 'none' } as any) : {}),
  },
  consentHint: {
    fontSize: 11.5,
    fontWeight: '500',
    marginTop: 3,
    letterSpacing: 0.1,
  },
  consentErrorText: {
    fontSize: 11.5,
    fontWeight: '700',
    marginTop: 3,
    letterSpacing: 0.1,
  },
  actionWrap: { marginTop: 18 },
  createBtn: {
    height: 46,
    minHeight: 46,
    borderRadius: 14,
    ...(Platform.OS === 'web'
      ? ({ boxShadow: '0 14px 28px rgba(11,11,13,0.32)' } as any)
      : { shadowColor: '#0B0B0D', shadowOffset: { width: 0, height: 12 }, shadowOpacity: 0.32, shadowRadius: 22, elevation: 10 }),
  },
  createBtnText: {
    fontSize: 15,
    letterSpacing: 1.2,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 8,
  },
  footerText: { fontSize: 14, fontWeight: '500' },
  footerLinkBtn: {
    width: 'auto' as any,
    minHeight: 'auto' as any,
    paddingHorizontal: 0,
  },
  footerLinkText: {
    textTransform: 'none' as any,
    fontWeight: '700',
    fontSize: 14,
    letterSpacing: 0,
  },
  footerDivider: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 14,
    marginBottom: 8,
    gap: 12,
  },
  footerRule: { flex: 1, height: 1 },
  footerDividerText: {
    fontSize: 10.5,
    fontWeight: '800',
    letterSpacing: 1.2,
  },
});

export default Signup_Screen;
